import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@artosera/ui';
import { PALIERS, TYPES_TARIF } from '@artosera/shared';
import type { TarifSaisie } from '../../types.js';
import { CelluleMontant, EtatOnglet, useMessageErreur } from './commun.js';
import { TableauSaisie, type ChampSaisie } from './TableauSaisie.js';
import { useSaisies } from './useSaisies.js';

/**
 * La grille tarifaire (direction ; l'exploitation la lit). Aucun prix n'est
 * écrit dans le code : la grille démarre vide et se remplit à la main. Elle
 * sert de valeur PROPOSÉE à la création d'un abonnement et de découpage des
 * forfaits par tranche ; elle ne modifie jamais un abonnement existant.
 */
export function TarifsTab({ enEcriture }: { enEcriture: boolean }): JSX.Element {
  const { t } = useTranslation('console');
  const message = useMessageErreur('tarifs');
  const { lignes, chargement, enErreur, creer, modifier, supprimer } = useSaisies('tarifs');

  const estPalier = (brouillon: Record<string, string | boolean>): boolean =>
    brouillon['type'] === 'palier';

  const champs: ChampSaisie[] = [
    {
      cle: 'type',
      titre: t('finances.tarifs.champs.type'),
      type: 'choix',
      requis: true,
      initiale: 'palier',
      options: TYPES_TARIF.map((type) => ({
        value: type,
        label: t(`finances.tarifs.type.${type}`),
      })),
    },
    {
      cle: 'libelle',
      titre: t('finances.tarifs.champs.libelle'),
      type: 'texte',
      requis: true,
      longueurMax: 80,
      aide: t('saisies.visibleAssocies'),
    },
    {
      cle: 'palier',
      titre: t('finances.tarifs.champs.palier'),
      type: 'choix',
      requis: true,
      visible: estPalier,
      options: PALIERS.map((palier) => ({ value: palier, label: t(`galeries.palier.${palier}`) })),
    },
    {
      cle: 'galeriesMin',
      titre: t('finances.tarifs.champs.min'),
      type: 'entier',
      requis: true,
      visible: (brouillon) => !estPalier(brouillon),
    },
    {
      cle: 'galeriesMax',
      titre: t('finances.tarifs.champs.max'),
      type: 'entier',
      aide: t('finances.tarifs.maxAide'),
      visible: (brouillon) => !estPalier(brouillon),
    },
    { cle: 'montantMensuelCentimes', titre: t('finances.tarifs.champs.montant'), type: 'montant' },
    {
      cle: 'ordre',
      titre: t('finances.tarifs.champs.ordre'),
      type: 'entier',
      videVaut: 0,
    },
    { cle: 'actif', titre: t('finances.tarifs.champs.actif'), type: 'booleen', initiale: true },
  ];

  const cible = (tarif: TarifSaisie): string => {
    if (tarif.type === 'palier') return tarif.palier ? t(`galeries.palier.${tarif.palier}`) : '—';
    if (tarif.galeriesMin === null) return '—';
    return tarif.galeriesMax === null
      ? t('finances.tarifs.trancheSansPlafond', { min: tarif.galeriesMin })
      : t('finances.tarifs.tranche', { min: tarif.galeriesMin, max: tarif.galeriesMax });
  };

  if (chargement || enErreur) return <EtatOnglet chargement={chargement} enErreur={enErreur} />;
  return (
    <TableauSaisie<TarifSaisie>
      legende={t('finances.onglets.tarifs')}
      introduction={t('finances.tarifs.texte')}
      lignes={lignes}
      colonnes={[
        {
          cle: 'type',
          titre: t('finances.tarifs.colonnes.type'),
          rendu: (ligne) => t(`finances.tarifs.type.${ligne.type}`),
        },
        {
          cle: 'libelle',
          titre: t('finances.tarifs.colonnes.libelle'),
          rendu: (ligne) => ligne.libelle,
        },
        { cle: 'cible', titre: t('finances.tarifs.colonnes.cible'), rendu: cible },
        {
          cle: 'montant',
          titre: t('finances.tarifs.colonnes.montant'),
          numerique: true,
          rendu: (ligne) => <CelluleMontant centimes={ligne.montantMensuelCentimes} />,
        },
        {
          cle: 'actif',
          titre: t('finances.tarifs.colonnes.actif'),
          rendu: (ligne) => (
            <Badge tone={ligne.actif ? 'available' : 'withdrawn'}>
              {ligne.actif ? t('finances.tarifs.actif') : t('finances.tarifs.inactif')}
            </Badge>
          ),
        },
        {
          cle: 'ordre',
          titre: t('finances.tarifs.colonnes.ordre'),
          numerique: true,
          rendu: (ligne) => ligne.ordre,
        },
      ]}
      champs={champs}
      valeursDe={(ligne) => ({
        type: ligne.type,
        libelle: ligne.libelle,
        palier: ligne.palier,
        galeriesMin: ligne.galeriesMin,
        galeriesMax: ligne.galeriesMax,
        montantMensuelCentimes: ligne.montantMensuelCentimes,
        ordre: ligne.ordre,
        actif: ligne.actif,
      })}
      libelleLigne={(ligne) => ligne.libelle}
      enEcriture={enEcriture}
      libelleAjouter={t('finances.tarifs.ajouter')}
      messageVide={t('finances.tarifs.vide')}
      sansMontant={lignes.filter((ligne) => ligne.montantMensuelCentimes === null).length}
      surCreer={creer}
      surModifier={(ligne, changements) => modifier(ligne.id, changements)}
      surSupprimer={(ligne) => supprimer(ligne.id)}
      message={message}
    />
  );
}
